import React, { useState, useEffect, useMemo } from 'react';
import { Column } from '@ant-design/plots';
import { Typography, Tabs } from 'antd';
import './style/BarChart.css';
import { useCountMediaByDateStore } from '../../../store/counters/countMediaByDateStore';
import { CountMedia } from '../../../types/CountMedia';

const colors = {
    Фото: '#5EC2B7',
    Видео: '#F25C5C',
    Аудио: '#F7B94A',
};

const { Title } = Typography;

const tabItems = [
    {
        key: 'all',
        label: (
            <span className="bar-chart-tab-label">
                Общее
            </span>
        ),
    },
    {
        key: 'Фото',
        label: (
            <span className="bar-chart-tab-label">
                <span className="bar-chart-tab-dot" style={{ background: colors.Фото }} />Фото
            </span>
        ),
    },
    {
        key: 'Видео',
        label: (
            <span className="bar-chart-tab-label">
                <span className="bar-chart-tab-dot" style={{ background: colors.Видео }} />Видео
            </span>
        ),
    },
    {
        key: 'Аудио',
        label: (
            <span className="bar-chart-tab-label">
                <span className="bar-chart-tab-dot" style={{ background: colors.Аудио }} />Аудио
            </span>
        ),
    },
];

function getLast7Days() {
    const days = [];
    const today = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    for (let i = 6; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        days.push({
            key: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
            label: d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
        });
    }
    return days;
}

const BarChart = () => {
    const [activeTab, setActiveTab] = useState('all');
    const fetchCountMediaByDate = useCountMediaByDateStore(s => s.fetchCountMediaByDate);
    const countMedia = useCountMediaByDateStore(s => s.countMedia);
    const loading = useCountMediaByDateStore(s => s.loading);

    const realData = useMemo(() => {
        if (!countMedia) return [];
        return countMedia.flatMap(item => [
            { day: new Date(item.day).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }), type: 'Фото', value: item.photo || 0 },
            { day: new Date(item.day).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }), type: 'Видео', value: item.video || 0 },
            { day: new Date(item.day).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }), type: 'Аудио', value: item.audio || 0 },
        ]);
    }, [countMedia]);

    useEffect(() => {
        fetchCountMediaByDate();
    }, []);

    let filteredData = realData;
    if (activeTab !== 'all') {
        filteredData = filteredData.filter(d => d.type === activeTab);
    }

    const daySums: Record<string, number> = {};
    filteredData.forEach(d => {
        if (!daySums[d.day]) daySums[d.day] = 0;
        daySums[d.day] += d.value;
    });
    const annotations = Object.entries(daySums).map(([day, value]) => ({
        type: 'text',
        data: [day, value],
        style: {
            text: `${value}`,
            textAlign: 'center',
            fontSize: 14,
            textBaseline: 'bottom',
            position: 'top',
        },
        xField: 'day',
        yField: 'value',
        tooltip: false,
    }));

    const config = {
        data: filteredData,
        xField: 'day',
        yField: 'value',
        colorField: 'type',
        legend: false,
        stack: true,
        scale: {
            color: {
                relations: [
                    ['Фото', colors.Фото],
                    ['Видео', colors.Видео],
                    ['Аудио', colors.Аудио],
                ],
            },
        },
        axis: {
            y: { labelFormatter: (v: number) => Number.isInteger(v) ? v.toString() : '' },
            x: { labelSpacing: 4 },
        },
        annotations,
    };

    return (
        <div className="bar-chart-container">
            <Title level={5} className="bar-chart-title" style={{ textAlign: 'left', width: '100%' }}>
                Количество записей за последние 7 дней
            </Title>
            <Tabs
                items={tabItems}
                activeKey={activeTab}
                onChange={setActiveTab}
                style={{ marginBottom: 16 }}
                className="bar-chart-tabs"
            />
            <div className="bar-chart-plot">
                <Column {...config} />
            </div>
        </div>
    );
};

export default BarChart; 